import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Stack from '@mui/joy/Stack';
import JoyInput from '@mui/joy/Input';
import JoyButton from '@mui/joy/Button';

import Card from '../../components/UI/Card/Card';
import ErrorModal from '../../components/UI/ErrorModal/ErrorModal';
import LoadingSpinner from '../../components/UI/LoadingSpinner/LoadingSpinner';
import {
  useSetupTwoFactorMutation,
  useEnableTwoFactorMutation,
  TwoFactorSetupResponse
} from '../../services/api';
import { isFetchBaseQueryError } from '../../utils/isFetchBaseQueryError';
import Athenalogo from '../../assets/images/Athena_logo_final.png';
import '../Login/Login.css';

interface LocationState {
  setupToken?: string;
  challengeToken?: string;
  email?: string;
  urlBeforeRedirect?: string;
}

const SetupTwoFactor = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setupToken, challengeToken, email, urlBeforeRedirect } =
    (state as LocationState | null) ?? {};

  const useEmailInstead = () => {
    if (!challengeToken) return;
    navigate('/2fa/challenge', {
      state: {
        challengeToken,
        hasTotp: false,
        email,
        urlBeforeRedirect,
        preselectedMethod: 'email' as const
      }
    });
  };

  const [code, setCode] = useState('');
  const [setupResult, setSetupResult] = useState<TwoFactorSetupResponse | null>(null);

  const [setupTwoFactor, { isLoading: isSetupLoading, error: setupError, reset: resetSetup }] =
    useSetupTwoFactorMutation();
  const [enableTwoFactor, { isLoading: isEnableLoading, error: enableError, reset: resetEnable }] =
    useEnableTwoFactorMutation();

  useEffect(() => {
    if (!setupToken) {
      navigate('/login', { replace: true });
    }
  }, [setupToken, navigate]);

  const startedRef = useRef(false);
  useEffect(() => {
    if (!setupToken || startedRef.current) return;
    startedRef.current = true;
    setupTwoFactor({ setupToken })
      .unwrap()
      .then(setSetupResult)
      .catch((err) => {
        console.log('[SetupTwoFactor]', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupToken]);

  const submitHandler = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (!setupToken || !/^\d{6}$/.test(code)) return;

    try {
      await enableTwoFactor({ setupToken, code }).unwrap();
      navigate(urlBeforeRedirect && urlBeforeRedirect !== '/login' ? urlBeforeRedirect : '/');
    } catch (err) {
      console.log('[SetupTwoFactor enable]', err);
    }
  };

  const error = setupError || enableError;
  const errorMessage =
    (isFetchBaseQueryError(error) ? (error.data as { message?: string } | undefined)?.message : '') ?? '';

  const isLoading = isSetupLoading || isEnableLoading;

  return (
    <div className="auth-container">
      <ErrorModal
        error={errorMessage}
        header="Two-Factor Setup Error"
        onClear={() => {
          resetSetup();
          resetEnable();
        }}
      />

      {isSetupLoading && !setupResult && (
        <div className="center" style={{ clear: 'both' }}>
          <LoadingSpinner />
        </div>
      )}

      {setupResult && (
        <Card className="authentication">
          <img src={Athenalogo} alt="logo" />
          <p className="headline">Set up authenticator app</p>
          <div style={{ padding: '0 5%' }}>
            <p style={{ fontSize: '0.95rem', color: '#363636' }}>
              Scan this QR code with your authenticator app (Google Authenticator, 1Password, Authy, etc.) and enter the
              6-digit code below to finish.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
              <img src={setupResult.qrCodeDataUrl} alt="2FA QR code" style={{ width: '200px', height: 'auto' }} />
            </div>
            {setupResult.secret && (
              <p style={{ fontSize: '0.8rem', color: '#666', wordBreak: 'break-all' }}>
                Can't scan? Enter this key manually: <code>{setupResult.secret}</code>
              </p>
            )}
          </div>
          <form onSubmit={submitHandler}>
            <Stack spacing={2} sx={{ padding: '0 5%', marginTop: '1rem' }}>
              <JoyInput
                id="code"
                type="text"
                slotProps={{
                  input: { inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, pattern: '\\d*' }
                }}
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                sx={{ fontSize: '1.1rem', textAlign: 'center' }}
              />
              <JoyButton type="submit" disabled={code.length !== 6 || isLoading} loading={isEnableLoading} fullWidth>
                Verify and enable
              </JoyButton>
              {challengeToken && (
                <JoyButton variant="plain" color="neutral" onClick={useEmailInstead} fullWidth>
                  Use email code instead
                </JoyButton>
              )}
              <JoyButton variant="plain" color="neutral" onClick={() => navigate('/login')} fullWidth>
                Back to login
              </JoyButton>
            </Stack>
          </form>
        </Card>
      )}
    </div>
  );
};

export default SetupTwoFactor;
